/**
 * Security Utilities for Web App
 * Handles decryption of encrypted API responses
 */

// Get the encryption key - MUST match server-side key derivation
// Server uses: crypto.createHash('sha256').update(secret).digest()
const getEncryptionKey = async (secret, operations = ['decrypt', 'encrypt']) => {
    const encoder = new TextEncoder();
    const secretBytes = encoder.encode(secret);
    
    // Hash the secret with SHA-256 to match server-side key derivation
    const hashBuffer = await crypto.subtle.digest('SHA-256', secretBytes);
    
    // Import the hash as an AES-GCM key
    return crypto.subtle.importKey(
        'raw',
        hashBuffer,
        { name: 'AES-GCM', length: 256 },
        false,
        operations
    );
};

// The encryption secret MUST match EXPO_PUBLIC_ENCRYPTION_SECRET on server and mobile
// IMPORTANT: Set REACT_APP_EXPO_PUBLIC_ENCRYPTION_SECRET in .env
const ENCRYPTION_SECRET = process.env.REACT_APP_EXPO_PUBLIC_ENCRYPTION_SECRET;

if (!ENCRYPTION_SECRET) {
    console.warn('REACT_APP_EXPO_PUBLIC_ENCRYPTION_SECRET not set. Encrypted responses will fail to decrypt.');
}

/**
 * Decrypt response from server
 * @param {Object} response - The encrypted response object with { encrypted: true, payload: string }
 * @returns {Object} - Decrypted data
 */
export const decryptResponse = async (response) => {
    // If not encrypted, return as-is
    if (!response || !response.encrypted || !response.payload) {
        return response;
    }
    
    try {
        if (!ENCRYPTION_SECRET) {
            console.error('Cannot decrypt: REACT_APP_EXPO_PUBLIC_ENCRYPTION_SECRET not configured');
            return response;
        }
        
        const parts = response.payload.split('.');
        if (parts.length !== 3) {
            console.error('Invalid encrypted payload format');
            return response;
        }
        
        const [ivBase64, authTagBase64, encryptedBase64] = parts;
        
        // Decode base64 parts
        const iv = Uint8Array.from(atob(ivBase64), c => c.charCodeAt(0));
        const authTag = Uint8Array.from(atob(authTagBase64), c => c.charCodeAt(0));
        const encrypted = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
        
        // Combine encrypted data with auth tag (WebCrypto expects them together)
        const encryptedWithTag = new Uint8Array(encrypted.length + authTag.length);
        encryptedWithTag.set(encrypted);
        encryptedWithTag.set(authTag, encrypted.length);
        
        const key = await getEncryptionKey(ENCRYPTION_SECRET);
        
        const decrypted = await crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: iv,
                tagLength: 128
            },
            key,
            encryptedWithTag
        );
        
        const decoder = new TextDecoder();
        const jsonString = decoder.decode(decrypted);
        return JSON.parse(jsonString);
    } catch (error) {
        console.error('Decryption failed:', error);
        // Return original response if decryption fails
        return response;
    }
};

/**
 * Check if response is encrypted
 * @param {Object} response - API response
 * @returns {boolean}
 */
export const isEncryptedResponse = (response) => {
    return response && response.encrypted === true && typeof response.payload === 'string';
};

/**
 * Encrypt data for sending to server
 * Uses AES-256-GCM to match server-side decryption
 * @param {Object} data - Data to encrypt
 * @returns {Object} - { encrypted: true, payload: "iv.authTag.encryptedData" }
 */
export const encryptRequest = async (data) => {
    if (!ENCRYPTION_SECRET) {
        console.error('Cannot encrypt: REACT_APP_EXPO_PUBLIC_ENCRYPTION_SECRET not configured');
        return data;
    }
    
    try {
        const key = await getEncryptionKey(ENCRYPTION_SECRET);
        
        // Generate random 12-byte IV for GCM
        const iv = crypto.getRandomValues(new Uint8Array(12));
        
        // Convert data to JSON string then to bytes
        const encoder = new TextEncoder();
        const jsonData = JSON.stringify(data);
        const dataBytes = encoder.encode(jsonData);
        
        // Encrypt using AES-GCM
        const encryptedBuffer = await crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv,
                tagLength: 128
            },
            key,
            dataBytes
        );
        
        // Split encrypted data and auth tag
        const encryptedArray = new Uint8Array(encryptedBuffer);
        const encryptedData = encryptedArray.slice(0, -16); // All except last 16 bytes
        const authTag = encryptedArray.slice(-16); // Last 16 bytes
        
        // Convert to base64
        const ivBase64 = btoa(String.fromCharCode(...iv));
        const authTagBase64 = btoa(String.fromCharCode(...authTag));
        const encryptedBase64 = btoa(String.fromCharCode(...encryptedData));
        
        return {
            encrypted: true,
            payload: `${ivBase64}.${authTagBase64}.${encryptedBase64}`
        };
    } catch (error) {
        console.error('Encryption failed:', error);
        return data;
    }
};

/**
 * Process API response - automatically decrypts if needed
 * @param {Object} response - API response
 * @returns {Object} - Processed (decrypted if needed) response
 */
export const processApiResponse = async (response) => {
    if (isEncryptedResponse(response)) {
        return await decryptResponse(response);
    }
    return response;
};

/**
 * Get the host URL for API calls
 * Uses the same pattern as common module for consistency
 * @param {Object} store - Redux store
 * @param {Object} config - Firebase config with projectId
 * @returns {string} - Host URL
 */
const getApiHost = (store, config) => {
    const settings = store?.getState()?.settingsdata?.settings;
    const host = (typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin)
        ? window.location.origin
        : `https://${config?.projectId || 'donkey-drive-5cdd1'}.web.app`;
    return host;
};

/**
 * Get a secure signed URL for PII documents
 * URLs expire after 15 minutes and cannot be shared
 * @param {string} uid - User ID who owns the document
 * @param {string} filePath - Storage path to the document
 * @param {string} idToken - Firebase Auth ID token
 * @param {Object} store - Redux store (optional, for getting settings)
 * @param {Object} config - Firebase config (optional, for projectId)
 * @returns {Promise<string|null>} - Signed URL or null if failed
 */
export const getSecureDocumentUrl = async (uid, filePath, idToken, store = null, config = null) => {
    if (!uid || !filePath || !idToken) {
        console.error('getSecureDocumentUrl: Missing required parameters');
        return null;
    }
    
    try {
        const host = getApiHost(store, config);
        const response = await fetch(
            `${host}/getSecureDocumentUrl`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ uid, filePath })
            }
        );
        
        if (!response.ok) {
            const error = await response.json();
            console.error('Failed to get secure URL:', error);
            return null;
        }
        
        const data = await response.json();
        return data.url;
    } catch (error) {
        console.error('Error fetching secure document URL:', error);
        return null;
    }
};

/**
 * Extract storage path from Firebase Storage URL
 * @param {string} url - Firebase Storage download URL
 * @returns {string|null} - Storage path or null
 */
export const extractStoragePath = (url) => {
    if (!url) return null;
    
    try {
        // Firebase Storage URLs look like:
        // https://firebasestorage.googleapis.com/v0/b/BUCKET/o/PATH?alt=media&token=TOKEN
        const match = url.match(/\/o\/([^?]+)/);
        if (match && match[1]) {
            // Decode the path (it's URL encoded)
            return decodeURIComponent(match[1]);
        }
        return null;
    } catch (error) {
        console.error('Error extracting storage path:', error);
        return null;
    }
};

export default {
    encryptRequest,
    decryptResponse,
    isEncryptedResponse,
    processApiResponse,
    getSecureDocumentUrl,
    extractStoragePath
};
